<template>
  <div
    :class="[$style.config, $style.card]"
    @click="(e) => $handleBlockLinkClick(e, cardLinkEl?.$el)"
  >
    <AspectRatio ratio="306x165">
      <BasePicture
        :src="data?.fields?.media?.fields?.file?.url + `?w=306&h=165&fit=thumb`"
        :alt="data?.fields?.media?.fields?.description || ''"
        loading="lazy"
      />
    </AspectRatio>
    <div :class="$style.contentContainer">
      <div>
        <p v-if="data?.fields?.brand?.fields?.label" :class="$style.brand">
          {{ data?.fields?.brand?.fields?.label }}
        </p>

        <BaseHeading size="h6" as="h3" :class="$style.name">
          <NuxtLinkLocale
            ref="cardLink"
            :to="`/products/${data?.fields?.path}`"
            target="_blank"
          >
            {{ data?.fields?.name }}
          </NuxtLinkLocale>
        </BaseHeading>
      </div>

      <em v-if="shouldShowReleaseDate" :class="$style.date">
        {{ localizedStrings['release-date'] || 'Release Date' }}
        {{ computedReleaseDate }}
      </em>
    </div>
  </div>
</template>

<script setup lang="ts">
import { parseISO } from 'date-fns'
import { formatLocalizedDate } from '../utils/localizeDates'

import type { TypeProductDetailsPageWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { locale } = useI18n()
const { localizedStrings } = useLocalizedStrings()

const cardLinkEl = useTemplateRef<ComponentPublicInstance | null>('cardLink')

const props = defineProps<{
  data: TypeProductDetailsPageWithoutUnresolvableLinksResponse | undefined
}>()

const computedReleaseDate = computed(() => {
  const releaseDate = props.data?.fields?.releaseDate?.split('T')?.[0]
  if (releaseDate) {
    return formatLocalizedDate(
      parseISO(releaseDate).toString(),
      'PP',
      locale.value,
    )
  }
  return ''
})

const shouldShowReleaseDate = computed(() => {
  return Boolean(computedReleaseDate.value && !props.data?.fields?.evergreen)
})
</script>

<style lang="scss" module>
.config {
  --content-container-font-size: 0.875rem;

  --name-font-size: 1.25rem;

  @include media('sm') {
    --name-font-size: 1.375rem;
  }
}

.card {
  display: flex;
  flex-direction: column;
  background-color: var(--white);
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 13px 0 rgba(0, 0, 0, 0.06);
  cursor: pointer;
}

.contentContainer {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 11px 20px 12px;
  text-align: center;
  font-size: var(--content-container-font-size);
  flex: 1;
}

.brand {
  color: var(--purple);
  font-size: to-rem(14px);
  font-weight: bold;
  line-height: math.div(22, 14);
  margin-bottom: 9px;
  text-align: center;
}

.name {
  font-family: var(--roboto-condensed);
  font-size: var(--name-font-size);
  line-height: math.div(28, 22);
  max-width: 220px;
  margin: 0 auto;

  a {
    color: inherit;
  }
}

.date {
  display: block;
  margin-top: 21px;
  font-weight: 400;
}
</style>
